'use client'

import Link from 'next/link'
import { usePathname } from 'next/navigation'
import type { ReactNode } from 'react'
import { useAuth } from './AuthProvider'

/**
 * Soft paywall: giriş yapmamış ziyaretçide haber gövdesinin başını gösterir,
 * devamı için giriş/üyelik çağrısı çıkarır. İçerik DOM'da kalır (SEO —
 * arama motorları tam metni görür), kilit yalnızca görseldir.
 * `enabled` false ise hiçbir ek katman render edilmez.
 */
export default function ArticlePaywallGate({
  enabled,
  children,
}: {
  enabled: boolean
  children: ReactNode
}) {
  const { user, isLoading } = useAuth()
  const pathname = usePathname()
  const locked = enabled && !isLoading && !user

  if (!locked) return <>{children}</>

  const redirect = encodeURIComponent(pathname || '/')

  return (
    <div className="relative">
      <div className="max-h-96 overflow-hidden">{children}</div>
      <div
        className="pointer-events-none absolute inset-x-0 bottom-0 h-48 bg-gradient-to-t from-white via-white/95 to-transparent dark:from-slate-950 dark:via-slate-950/95"
        aria-hidden
      />
      <div className="relative -mt-10 mb-8 rounded-2xl border border-gray-200 bg-white p-6 text-center shadow-lg sm:p-8 dark:border-slate-700 dark:bg-slate-900">
        <div className="mx-auto mb-3 flex size-11 items-center justify-center rounded-full bg-red-50 text-red-600 dark:bg-red-500/10 dark:text-red-400">
          <svg viewBox="0 0 24 24" fill="none" className="size-5" aria-hidden>
            <path
              stroke="currentColor"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
            />
          </svg>
        </div>
        <h2 className="text-lg font-bold text-gray-900 sm:text-xl dark:text-white">
          Haberin devamını okumak için giriş yapın
        </h2>
        <p className="mx-auto mt-1.5 max-w-md text-sm text-gray-600 dark:text-slate-400">
          Bu içeriğin tamamına yalnızca üyeler erişebilir. Ücretsiz üye olun
          veya hesabınızla giriş yapın.
        </p>
        <div className="mt-5 flex flex-col items-center justify-center gap-2.5 sm:flex-row">
          <Link
            href={`/login?redirect=${redirect}`}
            className="inline-flex w-full items-center justify-center rounded-xl bg-red-600 px-6 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-red-700 sm:w-auto"
          >
            Giriş Yap
          </Link>
          <Link
            href={`/register?redirect=${redirect}`}
            className="inline-flex w-full items-center justify-center rounded-xl border border-gray-300 px-6 py-2.5 text-sm font-semibold text-gray-800 transition-colors hover:bg-gray-50 sm:w-auto dark:border-slate-600 dark:text-slate-200 dark:hover:bg-slate-800"
          >
            Üye Ol
          </Link>
        </div>
      </div>
    </div>
  )
}
